import { pinaSelectors } from '../../shared/analytics.mjs';
import { cropLayers, restoreCropLayers, prepareRaster } from '../../shared/temporal.mjs';
import { DataLoadNotice, PORTAL_URL, ResearchTools, researchDataUrl, useSectionLinks, useSharedView, useDeferredSections, useDemandGeojson, readDemandGeojson } from '../../shared/ResearchTools.jsx';
import { fetchJson, featureKey } from '../../shared/research.mjs';
import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import * as maplibregl from 'maplibre-gl';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  ArcElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Chart as ChartJS,
} from 'chart.js';
import {
  Trees,
  TreePine,
  ShieldCheck,
  ShieldAlert,
  Play,
  Pause,
  RotateCcw,
  History,
  TrendingDown,
  TrendingUp,
  Building2,
  Globe,
  Download,
  Layers,
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  X,
  Eye,
  EyeOff,
  FileText,
  BarChart3,
  Compass,
  Info,
  MapPin,
  MapPinned,
  Sliders,
  Satellite,
  Menu,
  AlertTriangle,
  Map as MapIcon,
  Maximize2,
  Minimize2,
  Calendar,
  ExternalLink,
} from 'lucide-react';
const AboutModal = lazy(() => import('./components/AboutModal.jsx'));
const DashboardModal = lazy(() => import('./components/DashboardModal.jsx'));
const GeostoryModal = lazy(() => import('./components/GeostoryModal.jsx'));
const ReportModal = lazy(() => import('./components/ReportModal.jsx'));
import {
  formatHa,
  formatPct,
  numberFormat,
  preciseNumberFormat,
  normalizeKey,
  cleanFilename,
  YEARS,
  TOP_CANTONS,
  HISTORICAL_SOURCES,
  COLOR_SCALE,
  generateCantonKML,
  getGoogleEarthWebUrl,
  getFeatureCentroid,
} from './utils/pinaAnalytics';

ChartJS.register(
  ArcElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

const EMPTY_GEOJSON = { type: 'FeatureCollection', features: [] };

export default function App() {
  // Datos base cargados
  const [cantonesGeojson, setCantonesGeojson] = useState(EMPTY_GEOJSON);
  const [provinciasGeojson, setProvinciasGeojson] = useState(EMPTY_GEOJSON);
  const [areasProtegidasGeojson, setAreasProtegidasGeojson] = useState(EMPTY_GEOJSON);
  const [pina2000Geojson, setPina2000Geojson] = useState(EMPTY_GEOJSON);
  const [pina2015Geojson, setPina2015Geojson] = useState(EMPTY_GEOJSON);
  const [pina2019Geojson, setPina2019Geojson] = useState(EMPTY_GEOJSON);
  const [deforestacionGeojson, setDeforestacionGeojson] = useState(EMPTY_GEOJSON);
  const [pinaEnAspGeojson, setPinaEnAspGeojson] = useState(EMPTY_GEOJSON);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { kpis, provinces: PROVINCES } = pinaSelectors(analytics);

  // Filtros del panel lateral
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [filterTag, setFilterTag] = useState('all'); // 'all' | 'large' (>5000) | 'defor' (>500) | 'asp' (>0)

  // Selección de cantón
  const [selectedCanton, setSelectedCantonValue] = useState(null);
  const [sharedEntityId, setSharedEntityId] = useState('');
  const setSelectedCanton = useCallback((feature) => {
    setSelectedCantonValue(feature); setSharedEntityId(featureKey(feature));
  }, []);

  // Visibilidad de capas cartográficas
  const [layerVisibility, setLayerVisibility] = useState({
    cantones: true,
    provincias: true,
    areasProtegidas: false,
    pina2019: true,
    pina2015: false,
    pina2000: false,
    deforestacion: false,
    pinaEnAsp: false,
  });

  // Modales
  const [activeModal, setActiveModal] = useState('none'); // 'none' | 'geostory' | 'dashboard' | 'report' | 'about'

  // Tarjeta inferior multitemporal y satelital
  const [bottomCardExpanded, setBottomCardExpanded] = useState(true);
  const [activeBaseMap, setActiveBaseMap] = useState('satellite'); // 'satellite' | 'street'
  const [activeHistoricalSource, setActiveHistoricalSource] = useState('sentinel'); // 'sentinel' | 'wayback'
  const [selectedHistoricalYear, setSelectedHistoricalYear] = useState(2025);
  const [timelineYear, setTimelineYear] = useState(2019); // 2000, 2015, 2019
  const [isPlayingTimeline, setIsPlayingTimeline] = useState(false);
  const [mapReady, setMapReady] = useState(0);
  const [rasterStatus, setRasterStatus] = useState({ state: 'loading', requested: 2025, effective: null });
  const chooseYear = (year) => { setTimelineYear(year); setLayerVisibility((previous) => cropLayers(year, previous)); };
  const visibleCropYears = [2000, 2015, 2019].filter((year) => layerVisibility[`pina${year}`]);

  // Refs de mapa y temporizadores
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const timelineIntervalRef = useRef(null);
  const searchInputRef = useRef(null);
  const popupRef = useRef(null);

  const [loadErrors, setLoadErrors] = useState([]);
  const [criticalDataError, setCriticalDataError] = useState('');
  useSectionLinks(activeModal, setActiveModal);
  const loadedSections = useDeferredSections({ about: activeModal === 'about', dashboard: activeModal === 'dashboard', geostory: activeModal === 'geostory', report: activeModal === 'report' });
  useSharedView({
    entidad: { value: sharedEntityId, set: setSharedEntityId },
    provincia: { value: selectedProvince, set: setSelectedProvince }, buscar: { value: searchTerm, set: setSearchTerm },
    filtro: { value: filterTag, set: setFilterTag, validate: (value) => ['all', 'large', 'defor', 'asp'].includes(value) },
    anio: { value: timelineYear, set: setTimelineYear, validate: (year) => [2000, 2015, 2019].includes(year) },
    capas: { value: layerVisibility, set: setLayerVisibility, restore: restoreCropLayers },
    fondo: { value: activeBaseMap, set: setActiveBaseMap, validate: (value) => ['satellite', 'street'].includes(value) },
    proveedor: { value: activeHistoricalSource, set: setActiveHistoricalSource, validate: (value) => ['sentinel', 'wayback'].includes(value) },
    imagen_anio: { value: selectedHistoricalYear, set: setSelectedHistoricalYear, validate: (value) => HISTORICAL_SOURCES.sentinel.years.includes(value) },
  });

  useEffect(() => {
    if (loading) return;
    const feature = sharedEntityId ? (cantonesGeojson.features || []).find((entry) => featureKey(entry) === sharedEntityId) : null;
    setSelectedCantonValue(feature || null);
  }, [sharedEntityId, cantonesGeojson, loading]);

  // Carga de todos los datos en paralelo
  useEffect(() => {
    const controller = new AbortController();
    async function loadAllData() {
      try {
        setLoading(true);
        const failures = [];
        const load = async (path, fallback) => {
          try { return await fetchJson(researchDataUrl(path), { geojson: path.endsWith('.geojson'), signal: controller.signal }); }
          catch (error) {
            if (error.name === 'AbortError') throw error;
            if (['/data/cantones_display.geojson', '/data/analytics.json'].includes(path)) throw error;
            failures.push(error.message); return fallback;
          }
        };
        const [
          resCantones,
          resProvincias,
          resAsp,
          resP2000,
          resP2015,
          resP2019,
          resDefor,
          resPinaAsp,
          resAnalytics,
        ] = await Promise.all([
          load('/data/cantones_display.geojson', EMPTY_GEOJSON),
          load('/data/provincias_display.geojson', EMPTY_GEOJSON),
          Promise.resolve(EMPTY_GEOJSON),
          Promise.resolve(EMPTY_GEOJSON),
          Promise.resolve(EMPTY_GEOJSON),
          Promise.resolve(EMPTY_GEOJSON),
          Promise.resolve(EMPTY_GEOJSON),
          Promise.resolve(EMPTY_GEOJSON),
          load('/data/analytics.json', null),
        ]);

        setLoadErrors(failures);
        setCantonesGeojson({ ...resCantones, features: resCantones.features.map((feature) => ({ ...feature, properties: { ...feature.properties, ...(resAnalytics.cantons.find((row) => String(row.id) === String(feature.properties?.id ?? feature.properties?.cod_canton)) || {}) } })) });
        setProvinciasGeojson(resProvincias);
        setAreasProtegidasGeojson(resAsp);
        setPina2000Geojson(resP2000);
        setPina2015Geojson(resP2015);
        setPina2019Geojson(resP2019);
        setDeforestacionGeojson(resDefor);
        setPinaEnAspGeojson(resPinaAsp);
        setAnalytics(resAnalytics);
      } catch (err) {
        if (!controller.signal.aborted) setCriticalDataError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadAllData();
    return () => controller.abort();
  }, []);

  useDemandGeojson('/data/areas_protegidas.geojson', !loading && (layerVisibility.areasProtegidas || activeModal === 'geostory'), setAreasProtegidasGeojson, setLoadErrors);
  useDemandGeojson('/data/pina_2000_display.geojson', !loading && (layerVisibility.pina2000 || activeModal === 'geostory'), setPina2000Geojson, setLoadErrors);
  useDemandGeojson('/data/pina_2015_display.geojson', !loading && (layerVisibility.pina2015 || activeModal === 'geostory'), setPina2015Geojson, setLoadErrors);
  useDemandGeojson('/data/pina_2019_display.geojson', !loading && (layerVisibility.pina2019 || activeModal === 'geostory'), setPina2019Geojson, setLoadErrors);
  useDemandGeojson('/data/pina_deforestacion.geojson', !loading && (layerVisibility.deforestacion || activeModal === 'geostory'), setDeforestacionGeojson, setLoadErrors);
  useDemandGeojson('/data/pina_en_asp.geojson', !loading && (layerVisibility.pinaEnAsp || activeModal === 'geostory'), setPinaEnAspGeojson, setLoadErrors);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map?.getStyle()) return;
    map.getSource('asp-src')?.setData(areasProtegidasGeojson);
    map.getSource('pina2000-src')?.setData(pina2000Geojson);
    map.getSource('pina2015-src')?.setData(pina2015Geojson);
    map.getSource('pina2019-src')?.setData(pina2019Geojson);
    map.getSource('defor-src')?.setData(deforestacionGeojson);
    map.getSource('asp-invasion-src')?.setData(pinaEnAspGeojson);
  }, [mapReady, areasProtegidasGeojson, pina2000Geojson, pina2015Geojson, pina2019Geojson, deforestacionGeojson, pinaEnAspGeojson]);

  useEffect(() => { fetchJson(researchDataUrl('/data/display_manifest.json')).catch((error) => setLoadErrors((previous) => [...new Set([...previous, error.message])])); }, []);

  // Atajo de teclado: Tecla "/" enfoca el buscador
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/' && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape' && selectedCanton) {
        setSelectedCanton(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCanton]);

  // Lista de cantones enriquecida
  const cantonesList = useMemo(() => {
    return (cantonesGeojson.features || []).map((f) => {
      const p = f.properties || {};
      const id = String(p.id || p.cod_canton || '');
      const canton = p.canton || p.name || 'Cantón';
      const provincia = p.provincia || '';
      const area_ha = Number(p.area_ha || 0);
      const pina_2000 = Number(p.pina_2000 || 0);
      const pina_2015 = Number(p.pina_2015 || 0);
      const pina_2016 = Number(p.pina_2016 || 0);
      const pina_2017 = Number(p.pina_2017 || 0);
      const pina_2018 = Number(p.pina_2018 || 0);
      const pina_2019 = Number(p.pina_2019 || 0);
      const deforestacion_ha = Number(p.deforestacion_ha || 0);
      const asp_ha = Number(p.asp_ha || 0);
      const pct_canton = Number(p.pct_canton || (area_ha > 0 ? (pina_2019 / area_ha) * 100 : 0));
      const crecimiento = pina_2019 - pina_2000;

      return {
        id,
        rawFeature: f,
        canton,
        provincia,
        area_ha,
        pina_2000,
        pina_2015,
        pina_2016,
        pina_2017,
        pina_2018,
        pina_2019,
        deforestacion_ha,
        asp_ha,
        pct_canton,
        crecimiento,
      };
    });
  }, [cantonesGeojson]);

  // Filtrado de cantones en el panel lateral
  const filteredCantons = useMemo(() => {
    let result = cantonesList;

    if (searchTerm) {
      const term = normalizeKey(searchTerm);
      result = result.filter(
        (c) =>
          normalizeKey(c.canton).includes(term) ||
          normalizeKey(c.provincia).includes(term)
      );
    }

    if (selectedProvince) {
      const pKey = normalizeKey(selectedProvince);
      result = result.filter((c) => normalizeKey(c.provincia).includes(pKey));
    }

    if (filterTag === 'large') {
      result = result.filter((c) => c.pina_2019 >= 5000);
    } else if (filterTag === 'defor') {
      result = result.filter((c) => c.deforestacion_ha >= 500);
    } else if (filterTag === 'asp') {
      result = result.filter((c) => c.asp_ha > 0);
    }

    return [...result].sort((a, b) => b.pina_2019 - a.pina_2019);
  }, [cantonesList, searchTerm, selectedProvince, filterTag]);

  // Inicialización de MapLibre GL
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || loading) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          'osm-tiles': {
            type: 'raster',
            tiles: [
              'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors',
          },
          'satellite-tiles': {
            type: 'raster',
            tiles: [
              'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            ],
            tileSize: 256,
            attribution: '© Esri, Maxar, Earthstar Geographics',
          },
          'sentinel-tiles': {
            type: 'raster',
            tiles: [HISTORICAL_SOURCES.sentinel.getTileUrl(2025)],
            tileSize: 256,
            attribution: '© Copernicus Sentinel-2 (EOX)',
          },
        },
        layers: [
          {
            id: 'satellite-layer',
            type: 'raster',
            source: 'satellite-tiles',
            minzoom: 0,
            maxzoom: 19,
            layout: { visibility: 'none' },
          },
          {
            id: 'sentinel-layer',
            type: 'raster',
            source: 'sentinel-tiles',
            minzoom: 0,
            maxzoom: 19,
            layout: { visibility: 'visible' },
          },
          {
            id: 'street-layer',
            type: 'raster',
            source: 'osm-tiles',
            minzoom: 0,
            maxzoom: 19,
            layout: { visibility: 'none' },
          },
        ],
      },
      center: [-84.15, 10.05],
      zoom: 8.6,
      attributionControl: false,
    });

    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

    map.on('load', () => {
      setMapReady((value) => value + 1);
      // 1. Capa de Áreas Protegidas (SINAC - Azul Zafiro)
      if (areasProtegidasGeojson) {
        map.addSource('asp-src', {
          type: 'geojson',
          data: areasProtegidasGeojson,
        });
        map.addLayer({
          id: 'asp-fill',
          type: 'fill',
          source: 'asp-src',
          paint: {
            'fill-color': '#0284c7',
            'fill-opacity': 0.22,
          },
        });
        map.addLayer({
          id: 'asp-line',
          type: 'line',
          source: 'asp-src',
          paint: {
            'line-color': '#0284c7',
            'line-width': 1.6,
            'line-dasharray': [2, 1],
          },
        });
      }

      // 2. Capa de Piña 2000 (Línea base - Verde esmeralda)
      if (pina2000Geojson) {
        map.addSource('pina2000-src', {
          type: 'geojson',
          data: pina2000Geojson,
        });
        map.addLayer({
          id: 'pina2000-fill',
          layout: { visibility: layerVisibility.pina2000 ? 'visible' : 'none' },
          type: 'fill',
          source: 'pina2000-src',
          paint: {
            'fill-color': '#10b981',
            'fill-opacity': 0.65,
          },
        });
      }

      // 3. Capa de Piña 2015 (Amarillo dorado)
      if (pina2015Geojson) {
        map.addSource('pina2015-src', {
          type: 'geojson',
          data: pina2015Geojson,
        });
        map.addLayer({
          id: 'pina2015-fill',
          layout: { visibility: layerVisibility.pina2015 ? 'visible' : 'none' },
          type: 'fill',
          source: 'pina2015-src',
          paint: {
            'fill-color': '#eab308',
            'fill-opacity': 0.6,
          },
        });
      }

      // 4. Capa de Piña 2019 (Ámbar dorado)
      if (pina2019Geojson) {
        map.addSource('pina2019-src', {
          type: 'geojson',
          data: pina2019Geojson,
        });
        map.addLayer({
          id: 'pina2019-fill',
          layout: { visibility: layerVisibility.pina2019 ? 'visible' : 'none' },
          type: 'fill',
          source: 'pina2019-src',
          paint: {
            'fill-color': '#f59e0b',
            'fill-opacity': 0.65,
          },
        });
        map.addLayer({
          id: 'pina2019-line',
          layout: { visibility: layerVisibility.pina2019 ? 'visible' : 'none' },
          type: 'line',
          source: 'pina2019-src',
          paint: {
            'line-color': '#d97706',
            'line-width': 1.2,
          },
        });
      }

      // 5. Capa de Deforestación por Piña (Rojo Carmín)
      if (deforestacionGeojson) {
        map.addSource('defor-src', {
          type: 'geojson',
          data: deforestacionGeojson,
        });
        map.addLayer({
          id: 'defor-fill',
          type: 'fill',
          source: 'defor-src',
          paint: {
            'fill-color': '#ef4444',
            'fill-opacity': 0.8,
          },
        });
        map.addLayer({
          id: 'defor-line',
          type: 'line',
          source: 'defor-src',
          paint: {
            'line-color': '#b91c1c',
            'line-width': 1.6,
          },
        });
      }

      // 6. Capa de Piña dentro de ASP (Violeta / Carmín oscuro)
      if (pinaEnAspGeojson) {
        map.addSource('asp-invasion-src', {
          type: 'geojson',
          data: pinaEnAspGeojson,
        });
        map.addLayer({
          id: 'asp-invasion-fill',
          type: 'fill',
          source: 'asp-invasion-src',
          paint: {
            'fill-color': '#dc2626',
            'fill-opacity': 0.85,
          },
        });
        map.addLayer({
          id: 'asp-invasion-line',
          type: 'line',
          source: 'asp-invasion-src',
          paint: {
            'line-color': '#7f1d1d',
            'line-width': 2.2,
          },
        });
      }

      // 7. Capa de Cantones (Límites y selección)
      if (cantonesGeojson && cantonesGeojson.features?.length) {
        map.addSource('cantones-src', {
          type: 'geojson',
          data: cantonesGeojson,
        });
        map.addLayer({
          id: 'cantones-fill',
          type: 'fill',
          source: 'cantones-src',
          paint: {
            'fill-color': '#ffffff',
            'fill-opacity': 0.04,
          },
        });
        map.addLayer({
          id: 'cantones-line',
          type: 'line',
          source: 'cantones-src',
          paint: {
            'line-color': '#94a3b8',
            'line-width': 1.2,
          },
        });
        map.addLayer({
          id: 'cantones-selected-line',
          type: 'line',
          source: 'cantones-src',
          paint: {
            'line-color': '#fbbf24',
            'line-width': 3.5,
          },
          filter: ['==', ['get', 'canton'], ''],
        });

        // Eventos sobre cantones
        map.on('mousemove', 'cantones-fill', (e) => {
          if (!e.features?.length) return;
          map.getCanvas().style.cursor = 'pointer';
          const f = e.features[0];
          const p = f.properties || {};
          const cName = p.canton || p.name || 'Cantón';
          const prov = p.provincia || '';
          const pina19 = p.pina_2019 ? formatHa(p.pina_2019) : '0 ha';
          const defor = p.deforestacion_ha ? formatHa(p.deforestacion_ha) : '0 ha';
          const aspInvasion = p.asp_ha ? formatHa(p.asp_ha) : '0 ha';

          if (!popupRef.current) {
            popupRef.current = new maplibregl.Popup({
              closeButton: false,
              closeOnClick: false,
              offset: 12,
            });
          }
          popupRef.current
            .setLngLat(e.lngLat)
            .setHTML(`
              <div style="font-family: 'Outfit', sans-serif; font-size: 13px; line-height: 1.4; color: #0f172a; padding: 4px; min-width: 170px;">
                <div style="font-weight: 700; color: #0f172a; font-size: 14px;">${cName}</div>
                <div style="color: #64748b; font-size: 11px; margin-bottom: 6px;">${prov}</div>
                <div style="display: flex; justify-content: space-between; gap: 8px; margin-bottom: 2px;">
                  <span style="color: #d97706; font-weight: 600;">Piña 2019:</span>
                  <b>${pina19}</b>
                </div>
                <div style="display: flex; justify-content: space-between; gap: 8px; margin-bottom: 2px;">
                  <span style="color: #dc2626; font-weight: 600;">Deforestación:</span>
                  <b>${defor}</b>
                </div>
                ${
                  p.asp_ha > 0
                    ? `<div style="display: flex; justify-content: space-between; gap: 8px;">
                        <span style="color: #2563eb; font-weight: 600;">Piña en ASP:</span>
                        <b style="color: #2563eb;">${aspInvasion}</b>
                      </div>`
                    : ''
                }
              </div>
            `)
            .addTo(map);
        });

        map.on('mouseleave', 'cantones-fill', () => {
          map.getCanvas().style.cursor = '';
          if (popupRef.current) popupRef.current.remove();
        });

        map.on('click', 'cantones-fill', (e) => {
          if (!e.features?.length) return;
          const feat = e.features[0];
          setSelectedCanton(feat);
        });
      }

      // 8. Provincias (Límites provinciales gruesos)
      if (provinciasGeojson && provinciasGeojson.features?.length) {
        map.addSource('provincias-src', {
          type: 'geojson',
          data: provinciasGeojson,
        });
        map.addLayer({
          id: 'provincias-line',
          type: 'line',
          source: 'provincias-src',
          paint: {
            'line-color': '#cbd5e1',
            'line-width': 1.8,
            'line-opacity': 0.75,
          },
        });
      }
    });

    mapRef.current = map;

    return () => {
      if (popupRef.current) popupRef.current.remove();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [loading]);

  // Actualizar visibilidad de capas
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('sentinel-layer')) return;

    const setVis = (layerId, visible) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
      }
    };

    setVis('cantones-fill', layerVisibility.cantones);
    setVis('cantones-line', layerVisibility.cantones);
    setVis('provincias-line', layerVisibility.provincias);
    setVis('asp-fill', layerVisibility.areasProtegidas);
    setVis('asp-line', layerVisibility.areasProtegidas);
    setVis('pina2000-fill', layerVisibility.pina2000);
    setVis('pina2015-fill', layerVisibility.pina2015);
    setVis('pina2019-fill', layerVisibility.pina2019);
    setVis('pina2019-line', layerVisibility.pina2019);
    setVis('defor-fill', layerVisibility.deforestacion);
    setVis('defor-line', layerVisibility.deforestacion);
    setVis('asp-invasion-fill', layerVisibility.pinaEnAsp);
    setVis('asp-invasion-line', layerVisibility.pinaEnAsp);
  }, [layerVisibility, mapReady]);

  // Actualizar mapa base / mosaico satelital
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('sentinel-layer')) return;

    if (map.getLayer('satellite-layer') && map.getLayer('street-layer') && map.getLayer('sentinel-layer')) {
      if (activeBaseMap === 'street') {
        map.setLayoutProperty('street-layer', 'visibility', 'visible');
        map.setLayoutProperty('satellite-layer', 'visibility', 'none');
        map.setLayoutProperty('sentinel-layer', 'visibility', 'none');
      } else if (activeHistoricalSource === 'sentinel') {
        map.setLayoutProperty('street-layer', 'visibility', 'none');
        map.setLayoutProperty('satellite-layer', 'visibility', 'none');
        map.setLayoutProperty('sentinel-layer', 'visibility', 'visible');
      } else {
        map.setLayoutProperty('street-layer', 'visibility', 'none');
        map.setLayoutProperty('satellite-layer', 'visibility', 'visible');
        map.setLayoutProperty('sentinel-layer', 'visibility', 'none');
      }
    }
  }, [activeBaseMap, activeHistoricalSource, selectedHistoricalYear, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map?.getLayer('sentinel-layer') || activeBaseMap !== 'satellite' || activeHistoricalSource !== 'sentinel') return;
    return prepareRaster(map, selectedHistoricalYear, HISTORICAL_SOURCES.sentinel, (status) => setRasterStatus((previous) => ({ ...previous, ...status })));
  }, [mapReady, selectedHistoricalYear, activeBaseMap, activeHistoricalSource]);

  // Sincronizar resaltado y zoom al seleccionar un cantón
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('sentinel-layer')) return;

    const cName = selectedCanton?.properties?.canton || '';

    if (map.getLayer('cantones-selected-line')) {
      map.setFilter('cantones-selected-line', ['==', ['get', 'canton'], cName]);
    }

    if (selectedCanton && selectedCanton.geometry) {
      const bounds = new maplibregl.LngLatBounds();
      const visit = (coords) => {
        if (!coords) return;
        if (typeof coords[0] === 'number') {
          bounds.extend(coords);
          return;
        }
        coords.forEach(visit);
      };
      visit(selectedCanton.geometry.coordinates);
      map.fitBounds(bounds, { padding: 80, duration: 1600, maxZoom: 12.5 });
    }
  }, [selectedCanton]);

  // Reproductor temporal de evolución de capas de piña
  useEffect(() => {
    if (!isPlayingTimeline) {
      if (timelineIntervalRef.current) clearInterval(timelineIntervalRef.current);
      return;
    }

    const sequence = [2000, 2015, 2019];
    timelineIntervalRef.current = setInterval(() => {
      chooseYear(sequence[(sequence.indexOf(timelineYear) + 1) % sequence.length]);
    }, 2000);

    return () => {
      if (timelineIntervalRef.current) clearInterval(timelineIntervalRef.current);
    };
  }, [isPlayingTimeline, timelineYear]);

  // Resetear vista general de Costa Rica
  const handleResetMap = () => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({
      center: [-84.15, 10.05],
      zoom: 8.6,
      pitch: 0,
      bearing: 0,
      duration: 1500,
    });
    setSelectedCanton(null);
  };

  // Descarga de KML del cantón seleccionado
  const handleDownloadKML = async () => {
    if (!selectedCanton) return;
    const key = featureKey(selectedCanton);
    let original;
    try { original = (await readDemandGeojson('/data/cantones.geojson')).features.find((feature) => featureKey(feature) === key); }
    catch (error) { setLoadErrors((previous) => [...new Set([...previous, error.message])]); return; }
    if (!original) { setLoadErrors((previous) => [...previous, 'No se encontró la geometría original del cantón seleccionado.']); return; }
    const kmlContent = generateCantonKML(original);
    const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${cleanFilename(selectedCanton.properties?.canton || 'canton_costarica')}.kml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Enlace a Google Earth Web
  const handleOpenGoogleEarth = () => {
    if (!selectedCanton) return;
    const centroid = getFeatureCentroid(selectedCanton.geometry);
    const url = getGoogleEarthWebUrl(centroid);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Gráfico de evolución para el cantón seleccionado
  const cantonTrendData = useMemo(() => {
    if (!selectedCanton) return null;
    const p = selectedCanton.properties || {};
    return {
      labels: ['2000', '2015', '2016', '2017', '2018', '2019'],
      datasets: [
        {
          label: 'Superficie de Piña (ha)',
          data: [
            p.pina_2000 || 0,
            p.pina_2015 || 0,
            p.pina_2016 || 0,
            p.pina_2017 || 0,
            p.pina_2018 || 0,
            p.pina_2019 || 0,
          ],
          borderColor: '#d97706',
          backgroundColor: 'rgba(217, 119, 6, 0.15)',
          fill: true,
          tension: 0.35,
          pointBackgroundColor: '#b45309',
        },
      ],
    };
  }, [selectedCanton]);

  if (criticalDataError) return <main className="error-screen" role="alert"><h1>Datos del territorio no disponibles</h1><p>{criticalDataError}</p><p>No se mostrarán cifras como si la ausencia de datos fuera cero.</p><button type="button" onClick={() => window.location.reload()}>Reintentar</button><a href={PORTAL_URL}>Volver al geoportal</a></main>;

  return (
    <div className="app-shell">
      <DataLoadNotice loading={loading} errors={loadErrors} /><ResearchTools researchId="pina-costarica" context={{ entidad: sharedEntityId, provincia: selectedProvince, buscar: searchTerm, filtro: filterTag, anio: timelineYear }} errors={loadErrors} onOpenSection={setActiveModal} />
      {/* Topbar Institucional del Geoportal EIT */}
      <header className="topbar">
        <div className="topbar-identity">
          <a
            href={PORTAL_URL}
            className="geoportal-logo-link"
            title="Volver al Portal Principal Geoportal EIT"
          >
            <img
              src="/branding/Logo_Geoportal_Light_v2.png"
              alt="Geoportal EIT"
              className="geoportal-logo"
            />
          </a>

          <div className="topbar-divider" />

          <div className="brand">
            <div className="brand-mark bg-amber-500/15 text-amber-500">
              <Trees size={18} />
            </div>
            <div>
              <span>Costa Rica · Agroindustria y Territorio</span>
              <strong>Cultivo de Piña: Deforestación e Invasión en Áreas Protegidas (2000–2019)</strong>
            </div>
          </div>
        </div>

        {/* Indicadores Clave en Barra Superior */}
        <div className="topbar-kpis hidden lg:flex">
          <div className="topbar-kpi-item">
            <span className="kpi-num text-amber-500">{formatHa(kpis.pina_2019_ha)}</span>
            <span className="kpi-tag">Piña 2019 ({formatPct(kpis.growth_pct)})</span>
          </div>
          <div className="topbar-kpi-item">
            <span className="kpi-num text-red-500">{formatHa(kpis.deforestation_ha)}</span>
            <span className="kpi-tag">Deforestación</span>
          </div>
          <div className="topbar-kpi-item">
            <span className="kpi-num text-blue-400">{formatHa(kpis.protected_areas_invasion_ha)}</span>
            <span className="kpi-tag">En Áreas Protegidas</span>
          </div>
        </div>

        {/* Módulos de la Barra Superior */}
        <div className="topbar-actions">
          <button
            className={`topbar-nav-btn ${activeModal === 'none' ? 'active' : ''}`}
            onClick={() => setActiveModal('none')}
          >
            <MapIcon size={16} />
            <span>Geovisor</span>
          </button>

          <button
            className={`topbar-nav-btn ${activeModal === 'geostory' ? 'active' : ''}`}
            onClick={() => setActiveModal('geostory')}
          >
            <Compass size={16} />
            <span>Geohistoria</span>
          </button>

          <button
            className={`topbar-nav-btn ${activeModal === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveModal('dashboard')}
          >
            <BarChart3 size={16} />
            <span>Dashboard</span>
          </button>

          <button
            className={`topbar-nav-btn ${activeModal === 'report' ? 'active' : ''}`}
            onClick={() => setActiveModal('report')}
          >
            <FileText size={16} />
            <span>Reporte</span>
          </button>

          <button
            className={`topbar-nav-btn ${activeModal === 'about' ? 'active' : ''}`}
            onClick={() => setActiveModal('about')}
          >
            <Info size={16} />
            <span>Acerca de</span>
          </button>
        </div>
      </header>

      {/* Cuerpo Principal: Sidebar + Mapa WebGL */}
      <div className="app-main">
        {/* Barra Lateral / Inspector */}
        <aside className="sidebar">
          {selectedCanton ? (
            /* Ficha de Detalle del Cantón Seleccionado */
            <div className="inspector-panel">
              <div className="inspector-header">
                <button
                  className="btn-back-list"
                  onClick={() => setSelectedCanton(null)}
                >
                  <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
                  <span>Volver a la lista</span>
                </button>
                <button
                  className="btn-close-inspector"
                  onClick={() => setSelectedCanton(null)}
                >
                  <X size={16} />
                </button>
              </div>

              <div className="inspector-content">
                <div className="inspector-title-box">
                  <div className="inspector-eyebrow">Cantón de Costa Rica</div>
                  <h2>{selectedCanton.properties?.canton}</h2>
                  <div className="inspector-tags">
                    <span className="tag-loc">
                      <MapPin size={12} />
                      Provincia de {selectedCanton.properties?.provincia}
                    </span>
                    <span className="tag-title">
                      {formatHa(selectedCanton.properties?.area_ha)} sup. total
                    </span>
                  </div>
                </div>

                {/* Métricas Principales del Cantón */}
                <div className="inspector-kpi-grid">
                  <div className="insp-kpi-card highlight-amber">
                    <span>Piña Cultivada (2019)</span>
                    <strong>{formatHa(selectedCanton.properties?.pina_2019)}</strong>
                    <small>{formatPct(selectedCanton.properties?.pct_canton)} del cantón</small>
                  </div>

                  <div className="insp-kpi-card">
                    <span>Línea Base (2000)</span>
                    <strong>{formatHa(selectedCanton.properties?.pina_2000)}</strong>
                    <small>
                      {selectedCanton.properties?.pina_2019 > selectedCanton.properties?.pina_2000
                        ? `+${formatHa(selectedCanton.properties?.pina_2019 - selectedCanton.properties?.pina_2000)} neto`
                        : 'Sin variación'}
                    </small>
                  </div>

                  <div className="insp-kpi-card alert">
                    <span>Deforestación Neta</span>
                    <strong>{formatHa(selectedCanton.properties?.deforestacion_ha)}</strong>
                    <small>Bosques convertidos a piña</small>
                  </div>

                  <div className="insp-kpi-card highlight-blue">
                    <span>Piña en Áreas Protegidas</span>
                    <strong>{formatHa(selectedCanton.properties?.asp_ha)}</strong>
                    <small>Superposición SINAC</small>
                  </div>
                </div>

                {/* Gráfico de Evolución Multitemporal Cantonal */}
                {cantonTrendData && (
                  <div className="insp-chart-box">
                    <div className="insp-chart-header">
                      <TrendingUp size={14} className="text-amber-500" />
                      <span>Evolución Multitemporal de Piña (ha)</span>
                    </div>
                    <div className="insp-chart-render">
                      <Line
                        data={cantonTrendData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: {
                            y: {
                              beginAtZero: true,
                              grid: { color: 'rgba(255,255,255,0.05)' },
                              ticks: { font: { size: 10 } },
                            },
                            x: {
                              grid: { display: false },
                              ticks: { font: { size: 10 } },
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Dinámica de Suelo Cantonal */}
                <div className="insp-section">
                  <div className="insp-section-title">
                    <Layers size={14} className="text-amber-500" />
                    <span>Transiciones de Uso del Suelo</span>
                  </div>
                  <div className="insp-dynamics-list">
                    <div className="dynamic-item">
                      <span className="dynamic-dot bg-red-500" />
                      <div className="dynamic-info">
                        <strong>Deforestación Directa de Bosque:</strong>
                        <span>{formatHa(selectedCanton.properties?.deforestacion_ha)}</span>
                      </div>
                    </div>
                    <div className="dynamic-item">
                      <span className="dynamic-dot bg-orange-500" />
                      <div className="dynamic-info">
                        <strong>Conversión de Pastizales:</strong>
                        <span>{formatHa(selectedCanton.properties?.pastos_a_pina_ha)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botones de Acción 3D y KML */}
                <div className="inspector-actions">
                  <button className="btn-action-3d" onClick={handleOpenGoogleEarth}>
                    <Globe size={15} />
                    <span>Ver en 3D (Google Earth)</span>
                  </button>
                  <button className="btn-action-kml" onClick={handleDownloadKML}>
                    <Download size={15} />
                    <span>Descargar KML 3D</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Lista General de Cantones y Filtros */
            <div className="sidebar-catalog">
              <div className="sidebar-intro">
                <p className="eyebrow">Auditoría Cartográfica MOCUPP</p>
                <h1>84 Cantones de Costa Rica</h1>
                <p>
                  Monitoreo de expansión agroindustrial, pérdida de bosque primario e invasión en refugios del SINAC.
                </p>
              </div>

              {/* Filtros */}
              <div className="filter-box">
                <div className="search-field">
                  <Search size={16} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Buscar cantón o provincia... (/)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm ? (
                    <button onClick={() => setSearchTerm('')}><X size={14} /></button>
                  ) : (
                    <kbd>/</kbd>
                  )}
                </div>

                <div className="filter-field">
                  <label>Provincia</label>
                  <select
                    value={selectedProvince}
                    onChange={(e) => setSelectedProvince(e.target.value)}
                  >
                    <option value="">Todas las provincias (7)</option>
                    {PROVINCES.map((pr) => (
                      <option key={pr.name} value={pr.name}>
                        {pr.name} ({pr.pct}%)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Filtro Rápido por Chips */}
                <div className="filter-chips">
                  <button
                    className={`chip-btn ${filterTag === 'all' ? 'active' : ''}`}
                    onClick={() => setFilterTag('all')}
                  >
                    Todos ({cantonesList.length})
                  </button>
                  <button
                    className={`chip-btn ${filterTag === 'large' ? 'active' : ''}`}
                    onClick={() => setFilterTag('large')}
                  >
                    &gt;5,000 ha
                  </button>
                  <button
                    className={`chip-btn ${filterTag === 'defor' ? 'active' : ''}`}
                    onClick={() => setFilterTag('defor')}
                  >
                    &gt;500 ha defor.
                  </button>
                  <button
                    className={`chip-btn ${filterTag === 'asp' ? 'active' : ''}`}
                    onClick={() => setFilterTag('asp')}
                  >
                    Invasión en ASP
                  </button>
                </div>
              </div>

              {/* Lista de Resultados */}
              <div className="result-heading">
                <strong>{filteredCantons.length} Cantones</strong>
                <small>Ordenados por superficie de piña</small>
              </div>

              <div className="result-list">
                {filteredCantons.length === 0 ? (
                  <div className="no-results">
                    No se encontraron cantones con los filtros seleccionados.
                  </div>
                ) : (
                  filteredCantons.map((c) => (
                    <button
                      key={c.id || c.canton}
                      className="result-row"
                      onClick={() => setSelectedCanton(c.rawFeature)}
                    >
                      <div
                        className="result-color"
                        style={{
                          backgroundColor:
                            c.pina_2019 >= 10000
                              ? '#d97706'
                              : c.pina_2019 >= 4000
                              ? '#f59e0b'
                              : c.pina_2019 > 0
                              ? '#10b981'
                              : '#64748b',
                        }}
                      />
                      <div className="result-details">
                        <strong>{c.canton}</strong>
                        <small>
                          {c.provincia} · {formatHa(c.area_ha)} total
                        </small>
                      </div>
                      <div className="result-metrics">
                        <span className="loss-val font-mono text-amber-400">
                          {c.pina_2019 > 0 ? formatHa(c.pina_2019) : '0 ha'}
                        </span>
                        <span className="loss-pct text-red-400">
                          {c.deforestacion_ha > 0 ? `${formatHa(c.deforestacion_ha)} defor.` : '0 ha defor.'}
                        </span>
                      </div>
                      <ChevronRight size={14} className="result-arrow" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </aside>

        {/* Área del Geovisor Cartográfico */}
        <main className="map-area">
          {/* Barra Superior del Mapa: Toggles de Capas */}
          <div className="map-top-controls">
            <div className="map-layer-toggles">
              <button
                className={`layer-toggle-btn ${layerVisibility.cantones ? 'active' : ''}`}
                onClick={() =>
                  setLayerVisibility((p) => ({ ...p, cantones: !p.cantones }))
                }
              >
                <Building2 size={14} />
                <span>84 Cantones</span>
              </button>

              <button
                className={`layer-toggle-btn ${layerVisibility.areasProtegidas ? 'active' : ''}`}
                onClick={() =>
                  setLayerVisibility((p) => ({ ...p, areasProtegidas: !p.areasProtegidas }))
                }
              >
                <ShieldCheck size={14} />
                <span>Áreas Protegidas (SINAC)</span>
              </button>

              <button
                className={`layer-toggle-btn ${layerVisibility.pina2019 ? 'active' : ''}`}
                onClick={() =>
                  setLayerVisibility((p) => ({ ...p, pina2019: !p.pina2019 }))
                }
              >
                <span className="layer-dot bg-amber-500" />
                <span>Piña 2019</span>
              </button>

              <button
                className={`layer-toggle-btn ${layerVisibility.pina2000 ? 'active' : ''}`}
                onClick={() =>
                  setLayerVisibility((p) => ({ ...p, pina2000: !p.pina2000 }))
                }
              >
                <span className="layer-dot bg-emerald-500" />
                <span>Piña 2000</span>
              </button>

              <button
                className={`layer-toggle-btn ${layerVisibility.deforestacion ? 'active' : ''}`}
                onClick={() =>
                  setLayerVisibility((p) => ({ ...p, deforestacion: !p.deforestacion }))
                }
              >
                <span className="layer-dot bg-red-600" />
                <span>Deforestación ({formatHa(kpis.deforestation_ha)})</span>
              </button>

              <button
                className={`layer-toggle-btn ${layerVisibility.pinaEnAsp ? 'active' : ''}`}
                onClick={() =>
                  setLayerVisibility((p) => ({ ...p, pinaEnAsp: !p.pinaEnAsp }))
                }
              >
                <span className="layer-dot bg-red-800" />
                <span>Piña en ASP ({formatHa(kpis.protected_areas_invasion_ha)})</span>
              </button>
            </div>

            <button
              className="btn-reset-map"
              onClick={handleResetMap}
              title="Restablecer vista general de Costa Rica"
            >
              <RotateCcw size={14} />
              <span>Centrar</span>
            </button>
          </div>

          {/* Canvas WebGL MapLibre */}
          <div ref={mapContainerRef} className="maplibre-canvas-container" />

          {/* Tarjeta Inferior Multitemporal */}
          <section className={`bottom-card ${bottomCardExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="bottom-card-header">
              <div className="bottom-card-title">
                <History size={16} />
                <strong>Evolución Multitemporal y Teledetección Satelital</strong>
                <span className="badge-year">{visibleCropYears.length > 1 ? `Comparación ${visibleCropYears.join(' / ')}` : `Corte ${visibleCropYears[0] || 'sin capa'}`}</span>
              </div>

              <div className="bottom-card-actions">
                <button
                  className="btn-toggle-bottom"
                  onClick={() => setBottomCardExpanded((p) => !p)}
                  title={bottomCardExpanded ? 'Minimizar panel' : 'Expandir panel'}
                >
                  {bottomCardExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
              </div>
            </div>

            {bottomCardExpanded && (
              <div className="bottom-card-body">
                {/* Control de Tiempo (Slider + Play) */}
                <div className="timeline-control-group">
                  <div className="timeline-buttons">
                    <button
                      className={`btn-play-pause ${isPlayingTimeline ? 'playing' : ''}`}
                      onClick={() => setIsPlayingTimeline((p) => !p)}
                      title={isPlayingTimeline ? 'Pausar animación' : 'Reproducir evolución multitemporal (2000–2019)'}
                    >
                      {isPlayingTimeline ? <Pause size={15} /> : <Play size={15} />}
                      <span>{isPlayingTimeline ? 'Pausar' : 'Animar'}</span>
                    </button>
                    <button
                      className="btn-reset-timeline"
                      onClick={() => {
                        setIsPlayingTimeline(false);
                        chooseYear(2000);
                      }}
                      title="Reiniciar a 2000"
                    >
                      <RotateCcw size={14} />
                    </button>
                  </div>

                  <div className="timeline-slider-wrap">
                    <div className="timeline-slider-labels">
                      <span
                        className={`step-label ${timelineYear === 2000 ? 'active' : ''}`}
                        onClick={() => {
                          chooseYear(2000);
                        }}
                      >
                        2000
                      </span>
                      <span
                        className={`step-label ${timelineYear === 2015 ? 'active' : ''}`}
                        onClick={() => {
                          chooseYear(2015);
                        }}
                      >
                        2015
                      </span>
                      <span
                        className={`step-label ${timelineYear === 2019 ? 'active' : ''}`}
                        onClick={() => {
                          chooseYear(2019);
                        }}
                      >
                        2019
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={2}
                      step={1}
                      value={timelineYear === 2000 ? 0 : timelineYear === 2015 ? 1 : 2}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        const yr = val === 0 ? 2000 : val === 1 ? 2015 : 2019;
                        chooseYear(yr);
                      }}
                      className="timeline-range-input"
                    />
                  </div>
                </div>

                {/* Selector de Mosaicos Satelitales */}
                <div className="satellite-selector-group">
                  <div className="sat-type-pills">
                    <button
                      className={`sat-pill ${activeBaseMap === 'satellite' && activeHistoricalSource === 'sentinel' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveBaseMap('satellite');
                        setActiveHistoricalSource('sentinel');
                      }}
                    >
                      <Satellite size={13} />
                      <span>Sentinel-2 (10m)</span>
                    </button>

                    <button
                      className={`sat-pill ${activeBaseMap === 'satellite' && activeHistoricalSource === 'wayback' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveBaseMap('satellite');
                        setActiveHistoricalSource('wayback');
                      }}
                    >
                      <Globe size={13} />
                      <span>Esri World Imagery (fecha variable)</span>
                    </button>

                    <button
                      className={`sat-pill ${activeBaseMap === 'street' ? 'active' : ''}`}
                      onClick={() => setActiveBaseMap('street')}
                    >
                      <MapIcon size={13} />
                      <span>Calles OSM</span>
                    </button>
                  </div>

                  <p role={rasterStatus.state === 'error' ? 'alert' : 'status'}>Fondo: {activeBaseMap === 'street' ? 'OpenStreetMap' : activeHistoricalSource === 'wayback' ? 'Esri World Imagery · fecha variable' : `Sentinel-2 ${rasterStatus.effective ?? 'sin año confirmado'}`} {rasterStatus.state === 'loading' && activeHistoricalSource === 'sentinel' ? `· Cargando ${rasterStatus.requested}…` : ''}{rasterStatus.state === 'error' && activeHistoricalSource === 'sentinel' ? `· No se pudo cargar ${rasterStatus.requested}; se conserva el fondo anterior.` : ''}</p>
                  {activeBaseMap === 'satellite' && activeHistoricalSource === 'sentinel' && (
                    <select
                      className="sat-year-select"
                      value={selectedHistoricalYear}
                      onChange={(e) => setSelectedHistoricalYear(Number(e.target.value))}
                    >
                      {HISTORICAL_SOURCES.sentinel.years.map((yr) => (
                        <option key={yr} value={yr}>Sentinel-2 {yr}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}
          </section>
        </main>
      </div>

      {/* Los 4 Modales EIT */}
      {loadedSections.geostory && <Suspense fallback={<div className="eit-data-notice" role="status">Abriendo sección…</div>}> <GeostoryModal
        isOpen={activeModal === 'geostory'}
        onClose={() => setActiveModal('none')}
        cantonesGeojson={cantonesGeojson}
        provinciasGeojson={provinciasGeojson}
        areasProtegidasGeojson={areasProtegidasGeojson}
        pina2000Geojson={pina2000Geojson}
        pina2019Geojson={pina2019Geojson}
        deforestacionGeojson={deforestacionGeojson}
        pinaEnAspGeojson={pinaEnAspGeojson}
        onOpenDashboard={() => setActiveModal('dashboard')}
        onOpenReport={() => setActiveModal('report')}
        onSelectCanton={(feat) => setSelectedCanton(feat)}
      /> </Suspense>}

      {loadedSections.dashboard && <Suspense fallback={<div className="eit-data-notice" role="status">Abriendo sección…</div>}> <DashboardModal
        isOpen={activeModal === 'dashboard'}
        onClose={() => setActiveModal('none')}
        cantones={cantonesGeojson.features || []}
        analytics={analytics}
        areasProtegidas={areasProtegidasGeojson}
        onSelectCanton={(feat) => setSelectedCanton(feat)}
      /> </Suspense>}

      {loadedSections.report && <Suspense fallback={<div className="eit-data-notice" role="status">Abriendo sección…</div>}> <ReportModal
        isOpen={activeModal === 'report'}
        onClose={() => setActiveModal('none')}
        cantones={cantonesGeojson.features || []}
        analytics={analytics}
        areasProtegidas={areasProtegidasGeojson}
        selectedCanton={selectedCanton}
      /> </Suspense>}

      {loadedSections.about && <Suspense fallback={<div className="eit-data-notice" role="status">Abriendo sección…</div>}> <AboutModal
        isOpen={activeModal === 'about'}
        onClose={() => setActiveModal('none')}
      /> </Suspense>}
    </div>
  );
}
